import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppNavigate, usePaneLocation } from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import {
  Badge,
  Entity,
  EntityCard,
  EntityDataTable,
  getEntityType,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { capitalCase } from "change-case";
import { X } from "lucide-react";
import { useCallback } from "react";
import { useLocation } from "react-router";

export function EntityDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const paneLocation = usePaneLocation("rightPane");
  const location = useLocation();
  const entity = paneLocation?.state?.entity || location.state?.entity;

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  if (!entity) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                      aria-label="Close"
                    >
                      <X />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <div className="text-muted-foreground">Entity not found</div>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  return <EntityDetailContent entity={entity} />;
}

function EntityDetailContent({ entity }: { entity: Entity }) {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  const data = (entity.data || {}) as Record<string, unknown>;

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />

          <Badge variant="outline" className="shrink-0 mx-1">
            {capitalCase(getEntityType(entity))}
          </Badge>

          <div className="flex-1" />

          <TooltipProvider>
            <EntityPaneHeaderObjectActions
              entity={entity}
              name={capitalCase(getEntityType(entity))}
            />

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent className="!p-0">
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="px-4 pt-4 pb-2 border-b">
            <EntityCard
              entity={{
                ...entity,
                data: {
                  ...entity.data,
                  // @ts-ignore
                  web_link: null,
                },
              }}
              className="shadow-none border-none [&>div]:p-0 !bg-transparent"
              onMentionClick={undefined}
            />
          </div>

          <div className="px-4 py-4">
            <EntityDataTable data={data} />
          </div>
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
